import { useEffect, useRef } from 'react'
import { useAppStore, useSettingsStore, usePersonaStore } from '@/stores'
import { useAuthStore } from '@/stores/authStore'
import { useFeatureStore } from '@/stores/featureStore'
import { getRagConnector } from '@/services/ragConnector'
import { apiClient } from '@/services/apiClient'
import { startSettingsSync } from '@/services/settingsSync'
import { voiceConfigFromApi } from '@/utils/voiceConfigMapper'
import { elevenLabsProvider } from '@/connectors/voice/ElevenLabsProvider'
import { azureSpeechProvider } from '@/connectors/voice/AzureSpeechProvider'
import { tavusProvider } from '@/connectors/avatar/TavusProvider'
import { anamProvider } from '@/connectors/avatar/AnamProvider'
import type { Persona } from '@/stores'

/**
 * Credential service names used in the backend credentials table.
 */
const CREDENTIAL_SERVICES = {
  ELEVENLABS: 'elevenlabs',
  TAVUS: 'tavus',
  ANAM: 'anam',
  AZURE_SPEECH: 'azure-speech',
  RAG: 'rag',
  DEEPGRAM: 'deepgram',
  OPENAI: 'openai',
  STRIPE: 'stripe',
} as const

/** How long after a failed settings read the one retry waits (SA-1301). */
export const SETTINGS_RETRY_MS = 3000

type StoredSettings = Parameters<ReturnType<typeof useSettingsStore.getState>['loadSettings']>[0] & {
  preferences?: Parameters<ReturnType<typeof useAppStore.getState>['loadPreferences']>[0]
}

/** Clears a pending timer held in a ref. */
function clearTimer(ref: { current: ReturnType<typeof setTimeout> | null }) {
  if (ref.current !== null) clearTimeout(ref.current)
  ref.current = null
}

/** The viewer's stored settings, or null when they could not be read: a non-OK answer, a thrown fetch or a body that
 * will not parse (SA-1301). An empty object is a new user's answer, and a real one. */
async function readSettings(): Promise<StoredSettings | null> {
  try {
    const res = await apiClient.fetch('/api/settings')
    if (!res.ok) {
      console.warn('[AppInit] Settings read answered', res.status)
      return null
    }
    const { settings } = await res.json()
    return (settings ?? {}) as StoredSettings
  } catch (error) {
    console.warn('[AppInit] Failed to fetch settings:', error)
    return null
  }
}

function applySettings(
  settings: StoredSettings,
  loadSettings: ReturnType<typeof useSettingsStore.getState>['loadSettings'],
  loadPreferences: ReturnType<typeof useAppStore.getState>['loadPreferences'],
) {
  if (Object.keys(settings).length > 0) {
    console.debug('[AppInit] Received voiceFeatures from backend:', JSON.stringify(settings.voiceFeatures, null, 2))
    loadSettings({
      connections: settings.connections,
      voiceFeatures: settings.voiceFeatures,
      voiceConversation: settings.voiceConversation,
      ui: settings.ui,
    })
    if (settings.preferences) loadPreferences(settings.preferences)
    console.debug('[AppInit] Loaded settings from backend')
  } else {
    // Loaded even with empty settings (new user), and saveable
    loadSettings({})
    console.debug('[AppInit] No saved settings (new user), using defaults')
  }
}

function applyTheme() {
  if (useAppStore.getState().theme === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

/**
 * Hook to initialize the app with backend-stored settings on startup.
 * Loads user settings, credentials, and personas from the authenticated backend.
 */
export function useAppInit() {
  const { setInitializing, loadPreferences } = useAppStore()
  const {
    loadSettings,
    settleWithoutSettings,
    setRagApiKey,
    setCredentialStatus,
    setCredentialMeta,
    cacheCredential,
    cacheAzureCredential,
  } = useSettingsStore()
  const { setPersonas, setLoading: setPersonasLoading, setError: setPersonasError } = usePersonaStore()

  // SA-906: the app initialises for whoever is signed in — once per identity.
  // A View-as swaps `user` in place, and everything below (settings, keys,
  // personas, the settings sync) must be the target's, then the admin's again.
  const initializedFor = useRef<string | null>(null)
  // The one pending settings retry (SA-1301), and the run it belongs to. A new init and an unmount each start a new run,
  // so a retry from an earlier run is never scheduled or fired, even when the same user is back by then (a View-as
  // started and ended inside the wait). Never cleared in the init effect's cleanup: that effect re-runs on the
  // disclaimer flag, and its cleanup must not abort a live init.
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const initRun = useRef(0)
  useEffect(() => {
    const timer = retryTimer, runs = initRun
    return () => {
      runs.current += 1
      clearTimer(timer)
    }
  }, [])
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const impersonating = useAuthStore((s) => s.impersonation !== null)
  const mustAcceptDisclaimer = useAuthStore((s) => s.mustAcceptDisclaimer)

  useEffect(() => {
    // Don't kick off the heavy initial-data waterfall while the user is
    // still gated by the disclaimer modal — otherwise the AppShell paints
    // empty placeholders behind the modal, and we throw away the query
    // results when they accept anyway. The effect re-fires when the flag
    // flips false (after Accept) and runs the init for real.
    // Under a View-as the disclaimer is the target's obligation and the gate is
    // suppressed (spec D7), so initialisation must not wait on it.
    if (mustAcceptDisclaimer && !impersonating) return
    if (!userId || initializedFor.current === userId) return
    initializedFor.current = userId
    const run = ++initRun.current
    clearTimer(retryTimer)

    // Identity guard (SA-906): a View-as can start while this init is still
    // awaiting; the run for the previous identity must write nothing more.
    // Keyed on the ref, never on effect cleanup (the effect also re-runs for the
    // disclaimer flag, and that must not abort a live init).
    const forUser = userId
    const isStale = () => initializedFor.current !== forUser
    // The retry runs only in its own run, for the person it started for: a logout leaves `initializedFor` as it was
    // (SA-1301).
    const stillFor = () => initRun.current === run && useAuthStore.getState().user?.id === forUser

    const retrySettings = async () => {
      retryTimer.current = null
      if (!stillFor()) return
      const stored = await readSettings()
      if (!stillFor()) return
      if (stored) {
        applySettings(stored, loadSettings, loadPreferences)
        applyTheme()
      } else {
        settleWithoutSettings()
        console.warn('[AppInit] Settings could not be read; using the defaults in this tab, unsaved')
      }
    }

    const initializeApp = async () => {
      try {
        // 1. Load user settings from backend. A failed read loads nothing and saves nothing (SA-1301): one retry
        // follows, and the rest of the init goes on without waiting for it.
        const stored = await readSettings()
        if (isStale()) return
        if (stored) {
          applySettings(stored, loadSettings, loadPreferences)
        } else if (stillFor()) {
          retryTimer.current = setTimeout(() => { void retrySettings() }, SETTINGS_RETRY_MS)
        }

        // 2. Apply theme from loaded settings
        applyTheme()

        // 3. Load credential status and initialize providers
        // Build a set of services that have keys, so we can skip unnecessary key fetches
        const servicesWithKeys = new Set<string>()
        try {
          const credRes = await apiClient.fetch('/api/credentials')
          if (isStale()) return
          if (credRes.ok) {
            const credentials: Array<{
              service: string
              has_key: boolean
              config?: Record<string, unknown>
              source?: 'direct' | 'credential_set'
              credential_set_name?: string | null
              is_admin_managed?: boolean
              set_by_name?: string | null
            }> = await credRes.json()
            if (isStale()) return

            // Build credential metadata map
            const meta: Record<string, {
              source: 'direct' | 'credential_set'
              isAdminManaged: boolean
              credentialSetName?: string
              setByName?: string
            }> = {}
            for (const cred of credentials) {
              meta[cred.service] = {
                source: cred.source || 'direct',
                isAdminManaged: cred.is_admin_managed || false,
                credentialSetName: cred.credential_set_name || undefined,
                setByName: cred.set_by_name || undefined,
              }
            }
            setCredentialMeta(meta)

            for (const cred of credentials) {
              if (cred.has_key) servicesWithKeys.add(cred.service)

              // Set status flags for services WITHOUT keys immediately
              // (their status won't change later, so no race condition)
              // For services WITH keys, defer status until the key is actually cached
              // to avoid race conditions where hasElevenLabsKey=true but getElevenLabsKey()=undefined
              if (!cred.has_key) {
                if (cred.service === CREDENTIAL_SERVICES.ELEVENLABS) {
                  setCredentialStatus('elevenlabs', false)
                } else if (cred.service === CREDENTIAL_SERVICES.TAVUS) {
                  setCredentialStatus('tavus', false)
                } else if (cred.service === CREDENTIAL_SERVICES.ANAM) {
                  setCredentialStatus('anam', false)
                } else if (cred.service === CREDENTIAL_SERVICES.AZURE_SPEECH) {
                  setCredentialStatus('azure', false)
                } else if (cred.service === CREDENTIAL_SERVICES.RAG) {
                  setCredentialStatus('rag', false)
                } else if (cred.service === CREDENTIAL_SERVICES.DEEPGRAM) {
                  setCredentialStatus('deepgram', false)
                } else if (cred.service === CREDENTIAL_SERVICES.OPENAI) {
                  setCredentialStatus('openai', false)
                } else if (cred.service === CREDENTIAL_SERVICES.STRIPE) {
                  setCredentialStatus('stripe', false)
                }
              }
            }

            console.debug('[AppInit] Loaded credential status for', credentials.length, 'services')
          }
        } catch (error) {
          console.warn('[AppInit] Failed to fetch credentials:', error)
        }

        // 4. Initialize RAG connector from the deployment-level base
        // (`RAG_PROXY_BASE` exposed via /api/features → featureStore.ragProxyBase).
        // Falls back to '/rag-proxy' for pure-npm dev where Vite proxies it
        // to the local RAG server.
        const { ragProxyBase } = useFeatureStore.getState()
        const ragConnector = getRagConnector()
        ragConnector.setBaseUrl(ragProxyBase || '/rag-proxy')

        // SA-1034 PR 5 (spec §3.4): under a View-as the per-user key reads are
        // refused 403 `view_as_forbidden` by design (SA-906), so send none;
        // the statuses stay false, which is what the refusals produced. The
        // server-side resolver still finds the persona owner's key for chat.
        const viewAs = useAuthStore.getState().impersonation !== null
        if (viewAs) console.debug('[AppInit] key reads skipped under view-as')

        // Fetch RAG API key from backend and set on connector
        if (!viewAs && servicesWithKeys.has(CREDENTIAL_SERVICES.RAG)) {
          try {
            const ragKeyRes = await apiClient.fetch(`/api/credentials/${CREDENTIAL_SERVICES.RAG}/key`)
            if (isStale()) return
            if (ragKeyRes.ok) {
              const { api_key } = await ragKeyRes.json()
              if (isStale()) return
              setRagApiKey(api_key)
              ragConnector.setApiKey(api_key)
              setCredentialStatus('rag', true)
              console.debug('[AppInit] RAG API key loaded')
            } else {
              ragConnector.setApiKey('')
            }
          } catch {
            ragConnector.setApiKey('')
          }
        } else {
          ragConnector.setApiKey('')
        }

        // 5. Fetch personas from the backend
        try {
          setPersonasLoading(true)
          const response = await apiClient.fetch('/api/personas')
          if (isStale()) return
          if (response.ok) {
            const data = await response.json()
            if (isStale()) return
            const transformed: Persona[] = data.map((p: any) => ({
              id: p.id,
              name: p.name,
              description: p.description,
              avatarUrl: p.avatar_url,
              presentationMode: p.presentation_mode,
              voiceConfig: voiceConfigFromApi(p.voice_config),
              avatarConfig: p.avatar_config ? {
                provider: p.avatar_config.provider,
                replicaId: p.avatar_config.replica_id,
                personaId: p.avatar_config.persona_id,
                anamPersonaId: p.avatar_config.anam_persona_id,
                anamAvatarId: p.avatar_config.anam_avatar_id,
                anamVoiceId: p.avatar_config.anam_voice_id,
                anamLlmId: p.avatar_config.anam_llm_id,
                anamSystemPrompt: p.avatar_config.anam_system_prompt,
                anamEnableRecording: p.avatar_config.anam_enable_recording,
                anamEnableClientTools: p.avatar_config.anam_enable_client_tools,
                anamLanguageCode: p.avatar_config.anam_language_code,
                pipelineMode: p.avatar_config.pipeline_mode,
              } : undefined,
              ragConfig: {
                systemPrompt: p.rag_config.system_prompt,
                temperature: p.rag_config.temperature,
                maxTokens: p.rag_config.max_tokens,
                topK: p.rag_config.top_k,
                scoreThreshold: p.rag_config.score_threshold,
                serverUrl: p.rag_config.server_url,
                model: p.rag_config.model,
                apiKey: p.rag_config.api_key,
                graphId: p.rag_config.graph_id,
              },
              currentBundleId: p.current_bundle_id ?? null,
              whatsappConfig: p.whatsapp_config ? {
                enabled: p.whatsapp_config.enabled ?? false,
                greetingMessage: p.whatsapp_config.greeting_message,
                autoReply: p.whatsapp_config.auto_reply ?? true,
                voiceResponse: p.whatsapp_config.voice_response ?? false,
                voiceMessageMode: p.whatsapp_config.voice_message_mode ?? 'both',
                maxHistoryMessages: p.whatsapp_config.max_history_messages ?? 20,
                sessionTimeoutHours: p.whatsapp_config.session_timeout_hours ?? 12,
                contextBridgingEnabled: p.whatsapp_config.context_bridging_enabled ?? true,
                escalationConfig: p.whatsapp_config.escalation_config ? {
                  enabled: p.whatsapp_config.escalation_config.enabled ?? false,
                  triggerKeywords: p.whatsapp_config.escalation_config.trigger_keywords,
                } : undefined,
              } : undefined,
              isShared: p.is_shared || false,
              createdAt: new Date(p.created_at).getTime(),
              updatedAt: new Date(p.updated_at).getTime(),
            }))
            setPersonas(transformed)
            console.debug(`[AppInit] Loaded ${transformed.length} personas`)
          } else {
            // SA-1104: a refused read settles the store too, so a reader that
            // waits on it (the Inbox's bar) is never left waiting.
            setPersonasError(`Failed to fetch personas: ${response.status}`)
          }
        } catch (error) {
          console.warn('[AppInit] Failed to fetch personas:', error)
          if (!isStale()) setPersonasError('Failed to fetch personas')
        } finally {
          setPersonasLoading(false)
        }

        // 6. Initialize voice providers with backend-stored API keys
        // Only fetch keys for services that have credentials configured
        if (!viewAs && servicesWithKeys.has(CREDENTIAL_SERVICES.ELEVENLABS)) {
          try {
            const elKeyRes = await apiClient.fetch(`/api/credentials/${CREDENTIAL_SERVICES.ELEVENLABS}/key`)
            if (isStale()) return
            if (elKeyRes.ok) {
              const { api_key } = await elKeyRes.json()
              if (isStale()) return
              cacheCredential('elevenlabs', api_key)
              // Set status AFTER caching so getElevenLabsKey() returns a valid value
              // when useTextToSpeech reacts to hasElevenLabsKey changing to true
              setCredentialStatus('elevenlabs', true)
              await elevenLabsProvider.initialize(api_key)
              if (isStale()) return
              console.debug('[AppInit] ElevenLabs provider initialized')
            }
          } catch (error) {
            console.warn('[AppInit] Failed to initialize ElevenLabs:', error)
          }
        }

        if (!viewAs && servicesWithKeys.has(CREDENTIAL_SERVICES.AZURE_SPEECH)) {
          try {
            const azKeyRes = await apiClient.fetch(`/api/credentials/${CREDENTIAL_SERVICES.AZURE_SPEECH}/key`)
            if (isStale()) return
            if (azKeyRes.ok) {
              const data = await azKeyRes.json()
              if (isStale()) return
              const config = data.config as { region?: string } | null
              if (config?.region) {
                cacheAzureCredential(data.api_key, config.region)
                setCredentialStatus('azure', true)
                await azureSpeechProvider.initialize(`${data.api_key}:${config.region}`)
                if (isStale()) return
                console.debug('[AppInit] Azure Speech provider initialized')
              }
            }
          } catch {
            // Silently ignore — Azure Speech is optional
          }
        }

        if (!viewAs && servicesWithKeys.has(CREDENTIAL_SERVICES.TAVUS)) {
          try {
            const tavKeyRes = await apiClient.fetch(`/api/credentials/${CREDENTIAL_SERVICES.TAVUS}/key`)
            if (isStale()) return
            if (tavKeyRes.ok) {
              const { api_key } = await tavKeyRes.json()
              if (isStale()) return
              cacheCredential('tavus', api_key)
              setCredentialStatus('tavus', true)
              await tavusProvider.initialize(api_key)
              if (isStale()) return
              console.debug('[AppInit] Tavus provider initialized')
            }
          } catch (error) {
            console.warn('[AppInit] Failed to initialize Tavus:', error)
          }
        }

        // Anam API key is stored server-side only (proxied via backend).
        // We just set the status flag and lazily initialize on first use.
        if (servicesWithKeys.has(CREDENTIAL_SERVICES.ANAM)) {
          setCredentialStatus('anam', true)
          try {
            await anamProvider.initialize('')
            if (isStale()) return
            console.debug('[AppInit] Anam provider initialized')
          } catch (error) {
            console.warn('[AppInit] Failed to initialize Anam:', error)
          }
        }

        // Deepgram key is used server-side only (for ephemeral token generation).
        // We just need to set the status flag so the UI knows the feature is available.
        if (servicesWithKeys.has(CREDENTIAL_SERVICES.DEEPGRAM)) {
          setCredentialStatus('deepgram', true)
          console.debug('[AppInit] Deepgram credential detected')
        }

        // OpenAI key is used server-side only (for ephemeral token generation).
        // We just need to set the status flag so the UI knows Realtime mode is available.
        if (servicesWithKeys.has(CREDENTIAL_SERVICES.OPENAI)) {
          setCredentialStatus('openai', true)
          console.debug('[AppInit] OpenAI credential detected')
        }

        // Stripe key is used server-side only (for payment link creation).
        // We just need to set the status flag so the UI knows payments are available.
        if (servicesWithKeys.has(CREDENTIAL_SERVICES.STRIPE)) {
          setCredentialStatus('stripe', true)
          console.debug('[AppInit] Stripe credential detected')
        }

        // 7. Start settings sync (watches store changes → backend)
        if (isStale()) return
        startSettingsSync()

        console.debug('[AppInit] App initialization complete')
      } catch (error) {
        console.error('[AppInit] Initialization error:', error)
      } finally {
        if (!isStale()) setInitializing(false)
      }
    }

    initializeApp()
  }, [
    userId,
    impersonating,
    mustAcceptDisclaimer,
    loadSettings,
    settleWithoutSettings,
    loadPreferences,
    setRagApiKey,
    setCredentialStatus,
    setCredentialMeta,
    cacheCredential,
    cacheAzureCredential,
    setInitializing,
    setPersonas,
    setPersonasLoading,
    setPersonasError,
  ])
}
